import React from "react";
import { Box, Container, Typography, Link as MuiLink } from "@mui/material";
import SectionHeader from "../UI/SectionHeader";
import Vianaturgo from "../../assets/vianaturgo.png";
import Mpk from "../../assets/mpkbygg.png";

const projects = [
  {
    title: "MPKByggEntreprenad.se",
    year: "2024",
    desc: "A fully responsive, SEO-optimized landing page created for a Swedish construction company. Features multi-language support (Swedish/English) and integrated lead-generation forms.",
    stack: "React + TypeScript + Material UI",
    img: Mpk,
    url: "https://mpkbyggentreprenad.se",
    offset: false,
  },
  {
    title: "Interactive Learning Platform",
    year: "2023",
    desc: "A mobile, Udemy-style learning application where users can enroll in text-based courses, track progress, and complete interactive quizzes with automated scoring.",
    stack: "Flutter + Dart + Riverpod",
    img: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=2000&auto=format&fit=crop",
    url: "https://github.com/ahmad-hanafi1/quizzler_flutter",
    offset: true,
  },
  {
    title: "Vianaturgo.com",
    year: "2024",
    desc: "A high-performance supplements e-commerce platform. Engineered for speed and conversions, featuring a streamlined checkout flow and dynamic product management.",
    stack: "MERN Stack + Redux + Stripe",
    img: Vianaturgo,
    url: "https://vianaturgo.com",
    offset: false,
  },
];

const Works = () => {
  return (
    <Box
      id="works"
      sx={{ py: { xs: 8, md: 16 }, bgcolor: "rgba(18, 18, 20, 0.5)" }}
    >
      <Container maxWidth="lg" sx={{ px: 3 }}>
        <SectionHeader
          number="02"
          title="Selected Works"
          description="Engineering Case Studies"
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
            gap: { xs: 6, lg: 10 },
          }}
        >
          {projects.map((p, idx) => (
            <Box
              key={idx}
              sx={{
                position: "relative",
                mt: { lg: p.offset ? 12 : 0 },
                "&:hover img": { transform: "scale(1.05)", opacity: 1 },
                "&:hover h4": { color: "primary.main" },
              }}
            >
              {/* Image Container with Link overlay */}
              <MuiLink
                href={p.url}
                target="_blank"
                rel="noopener"
                sx={{ display: "block", textDecoration: "none" }}
              >
                <Box
                  sx={{
                    position: "relative",
                    aspectRatio: { xs: "4/3", sm: "16/9" },
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "custom.white5",
                    bgcolor: "custom.midnight",
                    mb: 3,
                    borderRadius: { xs: 1, md: 0 },
                  }}
                >
                  <Box
                    component="img"
                    src={p.img}
                    alt={p.title}
                    sx={{
                      objectFit: "cover",
                      width: "100%",
                      height: "100%",
                      transition: "transform 0.7s ease, opacity 0.7s ease",
                      opacity: { xs: 0.9, md: 0.7 },
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(to top, rgba(10, 10, 12, 0.8), transparent)",
                      display: { md: "none" },
                    }}
                  />
                  {/* External Link Icon for Desktop Hover */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 20,
                      right: 20,
                      bgcolor: "primary.main",
                      color: "custom.midnight",
                      p: 1,
                      display: { xs: "none", md: "flex" },
                      opacity: 0,
                      transition: "0.3s ease",
                      ".hover-trigger:hover &": { opacity: 1 },
                    }}
                    className="hover-trigger"
                  >
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: "20px" }}
                    >
                      open_in_new
                    </span>
                  </Box>
                </Box>
              </MuiLink>

              {/* Text Content */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", sm: "center" },
                    gap: 1,
                  }}
                >
                  <MuiLink
                    href={p.url}
                    target="_blank"
                    sx={{ textDecoration: "none", color: "inherit" }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontSize: { xs: "1.4rem", md: "1.875rem" },
                        fontWeight: 700,
                        transition: "color 0.3s",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {p.title}
                      <Box
                        component="span"
                        sx={{
                          display: { xs: "inline-block", md: "none" },
                          fontSize: "1.2rem",
                        }}
                        className="material-symbols-outlined"
                      >
                        north_east
                      </Box>
                    </Typography>
                  </MuiLink>
                  <Typography
                    sx={{
                      fontFamily: "mono",
                      fontSize: "0.7rem",
                      color: "text.secondary",
                      border: "1px solid",
                      borderColor: "custom.white10",
                      px: 1.2,
                      py: 0.4,
                      borderRadius: "4px",
                    }}
                  >
                    {p.year}
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    color: "text.secondary",
                    fontSize: { xs: "0.875rem", md: "0.93rem" },
                    lineHeight: 1.6,
                    maxWidth: "100%",
                  }}
                >
                  {p.desc}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "mono",
                      fontSize: "10px",
                      color: "primary.main",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      bgcolor: "rgba(0, 240, 255, 0.05)",
                      px: 1,
                      py: 0.5,
                      borderLeft: "2px solid",
                      borderColor: "primary.main",
                    }}
                  >
                    Stack: {p.stack}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default Works;
